import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import React, { forwardRef } from "react";
import * as cb from "@radix-ui/react-checkbox";
import type { FieldValues, Path, UseControllerReturn } from "react-hook-form";
import { cn } from "../../../utils/cn";

interface CheckboxProps {
  ref: React.RefCallback<HTMLButtonElement>;
  onBlur: () => void;
  name: string;
  onCheckedChange: (x: boolean) => void;
  checked: boolean;
}

export function getCheckboxProps<
  TInputs extends FieldValues,
  TName extends Path<TInputs>,
>(controller: UseControllerReturn<TInputs, TName>): CheckboxProps {
  return {
    ref: controller.field.ref,
    onBlur: controller.field.onBlur,
    name: controller.field.name,
    onCheckedChange: (x: boolean) => controller.field.onChange(x),
    checked: controller.field.value,
  };
}

const styles = cva(
  [
    "h-8 w-8 flex-shrink-0 block",
    "focus-visible:ring-[length:var(--control-ring-width-focus)] focus-visible:ring-control-ring-focus",
    "rounded outline-none border ring-0 ring-control-ring",
    "group-hover:ring-[length:var(--control-ring-width-hover)] group-hover:ring-control-ring-hover",
    "flex items-center justify-center overflow-hidden bg-gray-100 border-control-border",
  ],
  { variants: {}, defaultVariants: {} },
);

export interface StyledCheckboxProps
  extends cb.CheckboxProps,
    VariantProps<typeof styles> {
  className?: string;
  error?: boolean;
  children?: React.ReactNode;
}

export const StyledCheckbox = forwardRef<
  HTMLButtonElement,
  StyledCheckboxProps
>(function styledCheckbox({ className, error, ...rest }, ref) {
  return (
    <cb.Root
      className={cn(
        styles(),
        error && "border-control-border-error ring-control-ring-error",
        className,
      )}
      ref={ref}
      {...rest}
    >
      <cb.Indicator className="bg-primary-500 grid h-full w-full place-content-center">
        <div className="mb-[0.4em] h-[0.9em] w-[0.5em] rotate-45 border-r-2 border-b-2 border-white" />
      </cb.Indicator>
    </cb.Root>
  );
});

export function StyledCheckboxLabel(props: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className="font-heading col-start-2 text-lg font-semibold text-gray-700"
      {...props}
    />
  );
}

export function StyledCheckboxContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("group block flex items-center gap-5", className)}>
      {children}
    </label>
  );
}
